import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Music, TrendingUp, Target, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { staffApi } from "../lib/api";

export default function StaffDashboard() {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [checklist, setChecklist] = useState(null);
  const [unlockReqs, setUnlockReqs] = useState(null);

  useEffect(() => {
    staffApi.get("/staff/dashboard").then((r) => setD(r.data)).catch((e) => setError(e?.response?.data?.detail || "Could not load dashboard"));
    staffApi.get("/staff/onboarding-checklist").then((r) => setChecklist(r.data)).catch(() => {});
    staffApi.get("/staff/unlock-requests").then((r) => setUnlockReqs(r.data)).catch(() => setUnlockReqs({ requests: [], count: 0 }));
  }, []); // mount-only: initial dashboard fetch

  const resolveUnlock = async (id, action) => {
    try {
      await staffApi.post(`/staff/unlock-requests/${id}/resolve`, { action });
      setUnlockReqs((r) => ({ requests: r.requests.filter((x) => x.id !== id), count: Math.max(0, r.count - 1) }));
    } catch (e) { console.error("[ChoirQuest] unlock request resolve failed:", e); /* keep the row so staff can retry */ }
  };

  if (error) return <p className="text-[#A62B2B]" data-testid="dashboard-error">{error}</p>;
  if (!d) return <p className="text-[#566270]" data-testid="dashboard-loading">Loading dashboard…</p>;

  const stats = [
    { label: "Active choristers", value: d.active_choristers, icon: Users, testid: "stat-choristers" },
    { label: "Notes earned this week", value: d.notes_this_week.toLocaleString(), icon: Music, testid: "stat-notes" },
    { label: "Average mastery", value: `${d.avg_mastery}%`, icon: TrendingUp, testid: "stat-mastery" },
    { label: "Quests completed", value: d.quests_complete, icon: Target, testid: "stat-quests" },
  ];

  return (
    <div className="space-y-8" data-testid="staff-dashboard">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">{d.institution?.name} — Overview</h1>

      {unlockReqs && unlockReqs.count > 0 && (
        <section className="rounded-2xl bg-white border-2 border-[#C69B3D] p-6" data-testid="unlock-requests">
          <h2 className="font-display text-lg font-bold text-[#0B192C] flex items-center gap-2">
            Unlock requests
            <span className="px-2.5 py-0.5 rounded-full bg-[#7A1C2E] text-[#FAF7EE] text-xs font-bold" data-testid="unlock-requests-badge">{unlockReqs.count}</span>
          </h2>
          <div className="mt-4 space-y-3">
            {unlockReqs.requests.map((r) => (
              <div key={r.id} className="rounded-xl bg-[#FAF7EE] border border-[#E5DEC9] p-4" data-testid={`unlock-request-${r.id}`}>
                <p className="text-sm font-bold text-[#0B192C]">{r.learner_name}{r.group_name ? ` · ${r.group_name}` : ""}</p>
                <p className="text-sm text-[#1C242B] mt-0.5">{r.module_name} — {r.topic_name} · <span className="font-semibold">{r.item_label}</span></p>
                <p className="text-xs text-[#566270] mt-0.5">{new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button onClick={() => resolveUnlock(r.id, "chorister")} data-testid={`unlock-req-${r.id}-learner`}
                    className="px-3 py-2.5 rounded-xl bg-[#0B192C] text-[#FAF7EE] text-xs font-bold min-h-[44px]">Unlock for this learner</button>
                  {r.group_id && (
                    <button onClick={() => resolveUnlock(r.id, "group")} data-testid={`unlock-req-${r.id}-group`}
                      className="px-3 py-2.5 rounded-xl bg-[#2D6A4F] text-white text-xs font-bold min-h-[44px]">Unlock for group</button>
                  )}
                  <button onClick={() => resolveUnlock(r.id, "choir")} data-testid={`unlock-req-${r.id}-choir`}
                    className="px-3 py-2.5 rounded-xl bg-[#C69B3D] text-[#0B192C] text-xs font-bold min-h-[44px]">Unlock for whole choir</button>
                  <button onClick={() => resolveUnlock(r.id, "dismiss")} data-testid={`unlock-req-${r.id}-dismiss`}
                    className="px-3 py-2.5 rounded-xl border-2 border-[#E5DEC9] text-[#566270] text-xs font-bold min-h-[44px]">Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {checklist && checklist.items.some((i) => !i.done) && (
        <section className="rounded-2xl bg-[#0B192C] text-[#FAF7EE] p-6" data-testid="onboarding-checklist">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Getting started</h2>
            {checklist.trial?.type === "Trial" && checklist.trial?.expiry && (
              <span className="text-xs font-bold text-[#C69B3D]" data-testid="trial-badge">
                Trial ends {new Date(checklist.trial.expiry).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
              </span>
            )}
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {checklist.items.map((item) => (
              <Link key={item.key} to={item.link} data-testid={`checklist-${item.key}`}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors min-h-[48px] ${item.done ? "bg-[#2D6A4F]/20 text-[#FAF7EE]/60" : "bg-[#12253E] hover:bg-[#1E3A5F]"}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${item.done ? "bg-[#2D6A4F] text-white" : "border-2 border-[#C69B3D] text-transparent"}`}>✓</span>
                {item.label}
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {stats.map(({ label, value, icon: Icon, testid }) => (
          <div key={label} className="rounded-2xl bg-white border border-[#E5DEC9] p-5" data-testid={testid}>
            <Icon className="w-5 h-5 text-[#C69B3D]" aria-hidden />
            <p className="font-display text-2xl font-bold text-[#0B192C] mt-2">{value}</p>
            <p className="text-xs text-[#566270] font-semibold mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="topic-overview">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Topic overview</h2>
          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#2D6A4F] flex items-center gap-1"><ArrowUpRight className="w-3.5 h-3.5" /> Strongest</p>
              {d.strongest_topics.length === 0 && <p className="text-sm text-[#566270] mt-2">No data yet.</p>}
              {d.strongest_topics.map((t) => (
                <div key={t.topic} className="flex justify-between items-center mt-2 text-sm">
                  <span className="font-semibold text-[#0B192C]">{t.topic} <span className="text-[#566270] font-normal">· {t.module}</span></span>
                  <span className="font-bold text-[#2D6A4F]">{t.avg}%</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#7A1C2E] flex items-center gap-1"><ArrowDownRight className="w-3.5 h-3.5" /> Worth revisiting</p>
              {d.weakest_topics.length === 0 && <p className="text-sm text-[#566270] mt-2">No data yet.</p>}
              {d.weakest_topics.map((t) => (
                <div key={t.topic} className="flex justify-between items-center mt-2 text-sm">
                  <span className="font-semibold text-[#0B192C]">{t.topic} <span className="text-[#566270] font-normal">· {t.module}</span></span>
                  <span className="font-bold text-[#7A1C2E]">{t.avg}%</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="module-overview">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Module mastery</h2>
          <div className="mt-4 space-y-3">
            {d.module_stats.length === 0 && <p className="text-sm text-[#566270]">No learning activity yet.</p>}
            {d.module_stats.map((m) => (
              <div key={m.module}>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold text-[#0B192C]">{m.module}</span>
                  <span className="font-bold text-[#566270]">{m.avg}%</span>
                </div>
                <div className="h-2 rounded-full bg-[#F3EFE0] mt-1 overflow-hidden">
                  <div className="h-full rounded-full bg-[#0B192C]" style={{ width: `${m.avg}%` }} />
                </div>
              </div>
            ))}
          </div>
          {d.inactive_choristers.length > 0 && (
            <div className="mt-5 rounded-xl bg-[#F6EED8] border border-[#C69B3D]/40 p-4" data-testid="inactive-alert">
              <p className="text-xs font-bold uppercase tracking-widest text-[#8a6a10]">Not practised recently</p>
              <p className="text-sm text-[#1C242B] mt-1">{d.inactive_choristers.join(", ")}</p>
            </div>
          )}
        </section>
      </div>

      <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="grade-distribution">
        <h2 className="font-display text-lg font-bold text-[#0B192C]">Theory grade distribution</h2>
        <div className="flex flex-wrap gap-3 mt-3">
          {Object.entries(d.grade_distribution).map(([g, n]) => (
            <span key={g} className="px-4 py-2 rounded-full bg-[#0B192C] text-[#FAF7EE] text-sm font-bold">{g}: {n}</span>
          ))}
        </div>
      </section>

      <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="quest-snapshot">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Quest snapshot</h2>
          <Link to="/staff/quests" className="text-sm font-bold text-[#7A1C2E] underline" data-testid="view-all-quests">View all</Link>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#566270] border-b border-[#E5DEC9]">
                <th className="py-2 pr-4">Chorister</th><th className="py-2 pr-4">Quest</th>
                <th className="py-2 pr-4">Progress</th><th className="py-2 pr-4">Deadline</th><th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.quest_rows.slice(0, 8).map((r) => (
                <tr key={`${r.chorister}-${r.quest}`} className="border-b border-[#F3EFE0]">
                  <td className="py-2.5 pr-4 font-semibold text-[#0B192C]">{r.chorister}</td>
                  <td className="py-2.5 pr-4 text-[#1C242B]">{r.quest}</td>
                  <td className="py-2.5 pr-4 font-note">{Math.min(r.progress, r.target)}/{r.target}</td>
                  <td className="py-2.5 pr-4 text-[#566270]">{r.deadline ? new Date(r.deadline).toLocaleDateString("en-GB", { weekday: "short" }) : "—"}</td>
                  <td className="py-2.5 font-semibold text-[#0B192C]">{r.status}</td>
                </tr>
              ))}
              {d.quest_rows.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-[#566270]">No Quests yet — set one from the Quests page.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
